<template>
  <v-tooltip
    v-if='suppression'
    bottom
    :open-delay='400'
    content-class='suppression-badge-tooltip'
  >
    <template #activator='{ on, attrs }'>
      <span
        class='suppression-badge'
        :class='badgeClass'
        tabindex='0'
        role='note'
        :aria-label='ariaLabel'
        v-bind='attrs'
        v-on='on'
      >
        {{ label }}
      </span>
    </template>
    <div class='suppression-badge-tooltip__body'>
      <div class='suppression-badge-tooltip__title'>{{ typeLabel }}</div>
      <div>{{ $t('suppression.field.reason') }}: {{ reasonLabel }}</div>
      <div v-if='suppression.suppressedByName'>
        {{ $t('suppression.field.by') }}: {{ suppression.suppressedByName }}
      </div>
      <div>{{ $t('suppression.field.date') }}: {{ suppressedAt }}</div>
      <div>{{ expiryLine }}</div>
    </div>
  </v-tooltip>
</template>

<script>
import {
  daysUntilExpiry, formatSuppressionDate, isExpiringSoon, isTemporary,
} from '@/utils/suppression';
import { SUPPRESSION_REASONS } from '@/constants/suppression';

export default {
  name: 'SuppressionBadge',
  props: {
    suppression: {
      type: Object,
      default: null,
    },
    /** Table cell variant: no left margin, short label. */
    compact: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    temporary() {
      return isTemporary(this.suppression);
    },
    daysLeft() {
      return daysUntilExpiry(this.suppression);
    },
    badgeClass() {
      return {
        'suppression-badge--compact': this.compact,
        'suppression-badge--temporary': this.temporary,
        'suppression-badge--urgent': this.temporary && isExpiringSoon(this.suppression),
      };
    },
    label() {
      if (!this.temporary) {
        return this.$t('suppression.badge.suppressed');
      }
      if (this.daysLeft === 0) {
        return this.$t('suppression.badge.expires.today');
      }
      return this.$tc('suppression.badge.temporary', this.daysLeft, { days: this.daysLeft });
    },
    typeLabel() {
      return this.temporary ? this.$t('suppression.type.temporary') : this.$t('suppression.type.permanent');
    },
    reasonLabel() {
      const reason = this.$t(`suppression.reason.${this.suppression.reason}`);
      return this.suppression.reason === SUPPRESSION_REASONS.OTHER && this.suppression.reasonOther
        ? `${reason}: ${this.suppression.reasonOther}`
        : reason;
    },
    suppressedAt() {
      return formatSuppressionDate(this.suppression.suppressedAt);
    },
    expiryLine() {
      if (!this.temporary) {
        return this.$t('suppression.expiry.none');
      }
      return this.$t('suppression.expiry.on', { date: formatSuppressionDate(this.suppression.expiresAt) });
    },
    ariaLabel() {
      return `${this.label}. ${this.reasonLabel}. ${this.expiryLine}`;
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.suppression-badge {
  display: inline-block;
  margin-left: $space-2;
  padding: rem(1) rem(6);
  border-radius: $radius-md;
  background-color: rgba(119, 119, 119, 0.12);
  color: var(--text-secondary);
  font-family: $font-family-semibold;
  font-size: $font-size-xs;
  white-space: nowrap;
  cursor: default;

  &:focus-visible {
    outline: 2px solid var(--brand-primary);
    outline-offset: 2px;
  }

  &--compact {
    margin-left: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &--temporary {
    background-color: rgba(224, 173, 66, 0.16);
    color: #8a6116;
  }

  &--urgent {
    background-color: rgba(224, 173, 66, 0.32);
    color: #6b4a0e;
  }
}
</style>

<style lang='scss'>
@import '~@/assets/styles/design-system';

// The tooltip content is teleported to the app root, outside the scoped tree.
.suppression-badge-tooltip {
  transition: opacity 150ms ease-out, transform 150ms ease-out !important;
  transform-origin: top center;

  &__body {
    font-size: $font-size-sm;
    line-height: 1.5;
    max-width: rem(280);
  }

  &__title {
    font-family: $font-family-semibold;
    margin-bottom: rem(2);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: opacity 150ms ease-out !important;
    transform: none !important;
  }
}
</style>
